// Albella cart-drawer upsell (snippets/al-cart-upsell.liquid).
// Delegated listeners, so they keep working after the drawer section is morphed on every cart change.
// Slider: exactly ONE card per swipe / arrow / trackpad gesture, always landing square on a card
// (no native momentum scrolling). State lives in the rail's --al-i style, which the server renders as 0,
// so a cart re-render can never leave it half-way.
// Adds go through /cart/add.js with the cart sections attached, then a CartLinesUpdateEvent lets the theme's
// own components (drawer items, cart bubble) refresh exactly as they do for the product form.
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

// Theme is a top-level const in snippets/scripts.liquid (not on window), so read it by name.
// @ts-ignore
const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_URL = routes.cart_url || '/cart';

const SWIPE_MIN = 40; // px dragged → next/previous card
const FLICK_MIN = 15; // px for a quick flick…
const FLICK_SPEED = 0.35; // …faster than this (px per ms)
const FLICK_TIME = 250; // …or a whole swipe shorter than this (ms)
const START_SLOP = 8; // px before a touch counts as a swipe

if (!window.__alCartUpsell) {
  window.__alCartUpsell = true;

  /** @param {Element} rail */
  const cards = (rail) => /** @type {HTMLElement[]} */ ([...rail.querySelectorAll(':scope > [data-al-up-form]')]);

  /** @param {HTMLElement} rail */
  const getIndex = (rail) => {
    const n = parseInt(rail.style.getPropertyValue('--al-i'), 10);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Show card `index` (clamped to the first/last card).
   * @param {HTMLElement} rail @param {number} index @param {boolean} [animate]
   */
  const go = (rail, index, animate = true) => {
    const list = cards(rail);
    const i = Math.max(0, Math.min(list.length - 1, index));
    if (!animate) rail.classList.add('no-anim');
    rail.style.setProperty('--al-i', String(i));
    rail.style.removeProperty('--al-dx');
    const track = rail.parentElement;
    if (track) track.scrollLeft = 0;
    if (!animate) {
      rail.getBoundingClientRect();
      requestAnimationFrame(() => rail.classList.remove('no-anim'));
    }
    const root = rail.closest('[data-al-upsell]');
    const prev = root?.querySelector('[data-al-up-prev]');
    const next = root?.querySelector('[data-al-up-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = i <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = i >= list.length - 1;
    // only the visible card is focusable / read out
    list.forEach((card, k) => {
      card.inert = k !== i;
    });
    return i;
  };

  /** @param {Element | null} el */
  const railOf = (el) => /** @type {HTMLElement | null} */ (el?.closest('[data-al-upsell]')?.querySelector('[data-al-up-rail]') ?? null);

  // ---- arrows (no wrap-around: they grey out at the first / last card) ----
  let suppressClickUntil = 0;
  /** @type {{ el: Element | null, until: number }} */
  let tapDone = { el: null, until: 0 };
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const now = performance.now();
      if (tapDone.el && now < tapDone.until && target && tapDone.el.contains(target)) {
        // already handled on touch release (see "touch taps" below) - don't do it twice
        event.preventDefault();
        event.stopPropagation();
        tapDone = { el: null, until: 0 };
        return;
      }
      if (target?.closest('[data-al-up-track]') && now < suppressClickUntil) {
        // the click that ends a drag must not open a product or press Add
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = 0;
      }
    },
    true
  );

  /** Run what a tap on `el` should do. @param {Element} el */
  const activate = (el) => {
    if (el.matches('[data-al-up-prev], [data-al-up-next]')) {
      if (/** @type {HTMLButtonElement} */ (el).disabled) return;
      const rail = railOf(el);
      if (rail) go(rail, getIndex(rail) + (el.hasAttribute('data-al-up-next') ? 1 : -1));
    } else if (el.matches('[data-al-up-add]')) {
      const form = el.closest('form');
      if (form && !(/** @type {HTMLButtonElement} */ (el).disabled)) form.requestSubmit(/** @type {HTMLButtonElement} */ (el));
    } else if (el instanceof HTMLAnchorElement && el.href) {
      window.location.href = el.href;
    }
  };

  // ---- touch taps: act on finger release ----
  // Chrome on phones drops the click of a tap that lands while it still thinks a fling is running
  // (e.g. tapping Add or an arrow right after a fast swipe), so touch taps act on pointerup and the
  // late click, if it does arrive, is swallowed above. Mouse and keyboard keep using normal clicks.
  const TAP_TARGETS = '[data-al-up-prev], [data-al-up-next], [data-al-up-add], a.al-up__name, a.al-up__img';
  /** @type {null | { el: Element, id: number, x: number, y: number, t: number }} */
  let tap = null;
  document.addEventListener('pointerdown', (event) => {
    tap = null;
    if (event.pointerType === 'mouse') return;
    const target = event.target instanceof Element ? event.target : null;
    const el = target?.closest(TAP_TARGETS);
    if (!el || !el.closest('[data-al-upsell]')) return;
    tap = { el, id: event.pointerId, x: event.clientX, y: event.clientY, t: performance.now() };
  });
  document.addEventListener('pointerup', (event) => {
    const t = tap;
    tap = null;
    if (!t || event.pointerId !== t.id) return;
    if (drag?.active) return; // it was a swipe, not a tap
    if (Math.abs(event.clientX - t.x) > 10 || Math.abs(event.clientY - t.y) > 10) return;
    if (performance.now() - t.t > 700) return;
    tapDone = { el: t.el, until: performance.now() + 800 };
    activate(t.el);
  });
  document.addEventListener('pointercancel', () => {
    tap = null;
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const arrow = target?.closest('[data-al-up-prev], [data-al-up-next]');
    if (arrow) activate(arrow);
  });

  // ---- touch / mouse swipe: one card max per swipe ----
  /** @type {null | {rail: HTMLElement, track: HTMLElement, id: number, t0: number, x0: number, y0: number, dx: number, lastX: number, lastT: number, v: number, active: boolean}} */
  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    const track = /** @type {HTMLElement | null} */ (target?.closest('[data-al-up-track]') ?? null);
    // touch swipes may start on the size dropdown; a mouse press there opens it, so leave mouse alone
    if (!track || (event.pointerType === 'mouse' && target?.closest('select'))) return;
    const rail = /** @type {HTMLElement | null} */ (track.querySelector('[data-al-up-rail]'));
    if (!rail || cards(rail).length < 2) return;
    drag = {
      rail,
      track,
      id: event.pointerId,
      t0: event.timeStamp,
      x0: event.clientX,
      y0: event.clientY,
      dx: 0,
      lastX: event.clientX,
      lastT: event.timeStamp,
      v: 0,
      active: false,
    };
  });

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x0;
      const dy = event.clientY - drag.y0;
      if (!drag.active) {
        if (Math.abs(dx) < START_SLOP && Math.abs(dy) < START_SLOP) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          drag = null; // up/down: leave it to the drawer's own scrolling
          return;
        }
        drag.active = true;
        drag.rail.classList.add('is-dragging');
        try {
          drag.track.setPointerCapture(event.pointerId);
        } catch {}
      }
      const dt = event.timeStamp - drag.lastT;
      if (dt > 0) drag.v = 0.7 * ((event.clientX - drag.lastX) / dt) + 0.3 * drag.v;
      drag.lastX = event.clientX;
      drag.lastT = event.timeStamp;
      drag.dx = dx;

      const n = cards(drag.rail).length;
      const i = getIndex(drag.rail);
      const width = drag.track.clientWidth || 1;
      let offset = dx;
      if ((i === 0 && dx > 0) || (i === n - 1 && dx < 0)) offset = dx * 0.3; // resist at the ends
      offset = Math.max(-width, Math.min(width, offset)); // never more than one card of travel
      drag.rail.style.setProperty('--al-dx', `${offset}px`);
      if (event.cancelable) event.preventDefault();
    },
    { passive: false }
  );

  /** @param {PointerEvent} event @param {boolean} cancelled */
  const endDrag = (event, cancelled) => {
    if (!drag || event.pointerId !== drag.id) return;
    const d = drag;
    drag = null;
    if (!d.active) return;
    d.rail.classList.remove('is-dragging');
    let step = 0;
    if (!cancelled) {
      const speed = event.timeStamp - d.lastT < 120 ? d.v : 0;
      const quick = event.timeStamp - d.t0 < FLICK_TIME;
      if (d.dx <= -SWIPE_MIN || (d.dx <= -FLICK_MIN && (quick || speed < -FLICK_SPEED))) step = 1;
      else if (d.dx >= SWIPE_MIN || (d.dx >= FLICK_MIN && (quick || speed > FLICK_SPEED))) step = -1;
    }
    go(d.rail, getIndex(d.rail) + step);
    suppressClickUntil = performance.now() + 400;
  };
  document.addEventListener('pointerup', (event) => endDrag(event, false));
  document.addEventListener('pointercancel', (event) => endDrag(event, true));

  // ---- desktop trackpad sideways swipe: one card per gesture (ignores the inertia tail) ----
  let wheelLocked = false;
  let wheelSum = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let wheelTimer;
  document.addEventListener(
    'wheel',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const track = target?.closest('[data-al-up-track]');
      if (!track || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        wheelLocked = false;
        wheelSum = 0;
      }, 200);
      if (wheelLocked) return;
      wheelSum += event.deltaX;
      if (Math.abs(wheelSum) < 30) return;
      const rail = /** @type {HTMLElement | null} */ (track.querySelector('[data-al-up-rail]'));
      if (rail) go(rail, getIndex(rail) + (wheelSum > 0 ? 1 : -1));
      wheelLocked = true;
      wheelSum = 0;
    },
    { passive: false }
  );

  // ---- keyboard: tabbing onto a card brings it into view ----
  document.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('[data-al-up-form]');
    const rail = /** @type {HTMLElement | null} */ (card?.parentElement ?? null);
    if (!card || !rail?.matches('[data-al-up-rail]')) return;
    const k = cards(rail).indexOf(/** @type {HTMLElement} */ (card));
    if (k >= 0 && k !== getIndex(rail)) go(rail, k);
  });

  // ---- variant select updates the card price ----
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-al-up-variant]')) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-al-up-form]');
    if (!option || !card) return;
    const now = card.querySelector('.al-up__now');
    const compare = /** @type {HTMLElement | null} */ (card.querySelector('.al-up__compare'));
    if (now && option.dataset.price) now.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  });

  const refreshCart = async () => {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }
    const response = await fetch(`${CART_URL}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  };

  /**
   * After an add the drawer re-renders (slider back at card 0, added product gone).
   * Put the slider back where the shopper was, without animating, so they see the next product.
   * @param {string | undefined} productId @param {number} keep
   */
  const restorePosition = (productId, keep) => {
    const started = performance.now();
    const check = () => {
      const rail = /** @type {HTMLElement | null} */ (document.querySelector('[data-al-up-rail]'));
      if (!rail) return;
      const stillThere = productId && rail.querySelector(`[data-product-id="${productId}"]`);
      if (!stillThere) {
        go(rail, keep, false);
        return;
      }
      if (performance.now() - started < 3000) requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  };

  // ---- Add ----
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-al-up-form]')) return;
    event.preventDefault();

    const button = form.querySelector('[data-al-up-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const label = button.textContent;
    button.disabled = true;

    const rail = railOf(form);
    const keep = rail ? getIndex(rail) : 0;

    const formData = new FormData(form);
    const sectionIds = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    }
    if (sectionIds.size) formData.append('sections', [...sectionIds].join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(CART_ADD_URL, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const cart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'al-cart-upsell',
          itemCount: 1,
          sections: response.status ? undefined : response.sections,
          didError: Boolean(response.status),
        },
      });

      if (response.status) {
        button.textContent = response.message || label;
        setTimeout(() => {
          button.textContent = label;
          button.disabled = false;
        }, 2500);
        return;
      }
      restorePosition(form.dataset.productId, keep);
    } catch (error) {
      console.error('[al-cart-upsell]', error);
      deferred.reject(error);
    }

    // On success the drawer re-renders and this card disappears (the product is now in the cart).
    if (button.isConnected) {
      button.disabled = false;
      button.textContent = label;
    }
  });
}
